import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { toast } from 'sonner';
import {
  Save,
  X,
  RotateCcw,
  Settings2,
  Plug2,
  CheckCircle2,
  AlertCircle,
  Database,
  FileEdit,
  ArrowRight,
  Languages,
} from 'lucide-react';
import type { AssetOptions, NominalAccount, CostCentre, PeriodPatternPreset } from '@/types/settings';
import { PERIOD_PATTERN_PRESETS } from '@/types/settings';
import type {
  Integration,
  IntegrationType,
  DatabaseInstance,
  IntegrationConfig
} from '@/types/integrations';
import { mockIntegrations, mockDatabaseInstances } from '@/types/integrations';
import { cn } from '@/lib/utils';
import { dataService } from '@/services/data-service';
import { financialsService } from '@/services/financials-graphql-service';
import { useIntegration } from '@/providers/IntegrationProvider';
import type { CostCentre as FinancialsCostCentre, NominalAccount as FinancialsNominalAccount } from '@/types/financials';
import {
  DetailsTab,
  TransactionsTab,
  PeriodsTab,
  IntegrationsTab,
  LanguageTab,
} from '@/components/settings/asset-options';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { LanguageSetting } from '@/components/settings/asset-options';

// Mock data for nominal accounts and cost centres
const mockNominalAccounts: NominalAccount[] = [
  { code: '1000', name: 'Fixed Assets - Land & Buildings' },
  { code: '1100', name: 'Fixed Assets - Plant & Machinery' },
  { code: '1200', name: 'Fixed Assets - Vehicles' },
  { code: '1300', name: 'Fixed Assets - IT Equipment' },
  { code: '1400', name: 'Fixed Assets - Furniture & Fittings' },
  { code: '2000', name: 'Accumulated Depreciation - Buildings' },
  { code: '2100', name: 'Accumulated Depreciation - Machinery' },
  { code: '2200', name: 'Accumulated Depreciation - Vehicles' },
  { code: '3000', name: 'Depreciation Expense' },
  { code: '3100', name: 'Depreciation - Buildings' },
  { code: '3200', name: 'Depreciation - Equipment' },
  { code: '4000', name: 'Disposal of Assets' },
  { code: '4100', name: 'Gain/Loss on Disposal' },
  { code: '5000', name: 'Revaluation Reserve' },
  { code: '5100', name: 'Revaluation Surplus' },
  { code: '6000', name: 'Bank Account - Main' },
  { code: '6100', name: 'Bank Account - Disposals' },
  { code: '7000', name: 'Work in Progress' },
  { code: '7100', name: 'Capital WIP' },
];

const mockCostCentres: CostCentre[] = [
  { code: 'CC001', name: 'Head Office' },
  { code: 'CC002', name: 'Manufacturing' },
  { code: 'CC003', name: 'Sales & Marketing' },
  { code: 'CC004', name: 'IT Department' },
  { code: 'CC005', name: 'Finance' },
  { code: 'CC006', name: 'Human Resources' },
  { code: 'CC007', name: 'Research & Development' },
  { code: 'CC008', name: 'Warehouse' },
  { code: 'CC009', name: 'Customer Service' },
  { code: 'CC010', name: 'Administration' },
];

const defaultOptions: AssetOptions = {
  general: {
    assetAutoNumbering: false,
    doNotAllowNegativeBoughtValue: false,
    defaultBaseValueOn: 'remaining',
    servicePeriodDefaults: 0,
  },
  promptNominalUpdate: {
    depreciation: false,
    addition: false,
    revaluation: false,
    disposal: false,
    transfer: false,
  },
  updateNominal: {
    includeResourceOnNominalJournals: false,
    summariseJournals: true,
  },
  transactions: {
    allowZeroValueAdditions: false,
    forceAllDepreciationBeforePeriodEnd: false,
    doNotReverseInPeriodDepreciation: false,
    nominalAccounts: {
      assetCost: undefined,
      depreciationBS: undefined,
      depreciationPL: undefined,
      disposal: undefined,
      revaluationBS: undefined,
      revaluationPL: undefined,
      disposalsBankAccount: undefined,
      additionsWIPBankAcc: undefined,
    },
    costing: {
      depreciation: undefined,
      addition: undefined,
      disposals: undefined,
      revaluation: undefined,
    },
    postCostingTransactions: {
      depreciation: false,
      additions: false,
      revaluations: false,
      disposals: false,
    },
    postCreditToCosting: {
      depreciation: false,
      additions: false,
      revaluations: false,
      disposals: false,
    },
  },
  defaultSettings: {
    defaultMethod: 'Straight Line',
    defaultPercentage: 20.0,
    defaultPeriodYears: 5,
    defaultFrequency: 'Monthly',
    doNotDepreciate: false,
    forceTransfersOnChangingCategory: false,
  },
  periods: {
    preset: '4-4-5' as PeriodPatternPreset,
    periodRatios: PERIOD_PATTERN_PRESETS['4-4-5'].ratios,
  },
};

const defaultIntegrationConfig: IntegrationConfig = {
  activeIntegration: undefined,
  integrations: {}
};

export default function AssetOptionsPage(): React.ReactElement {
  const { settings: integrationSettings, setSelectedInstance: setIntegrationContextInstance, clearSelectedInstance: clearIntegrationContextInstance } = useIntegration();
  const hasInitializedFromApiRef = useRef(false);
  const [options, setOptions] = useState<AssetOptions>(defaultOptions);
  const [integrationConfig, setIntegrationConfig] = useState<IntegrationConfig>(defaultIntegrationConfig);
  const [integrations] = useState<Integration[]>(mockIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationType | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadedInstances, setLoadedInstances] = useState<Record<IntegrationType, DatabaseInstance[]>>({
    'access-dimensions': [],
    'access-financials': [],
    'xero': [],
    'quickbooks': []
  });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
  const [savedOptions, setSavedOptions] = useState<AssetOptions>(defaultOptions);
  const [savedIntegrationState, setSavedIntegrationState] = useState<{
    integration: IntegrationType | null;
    instance: string;
  }>({ integration: null, instance: '' });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const [costCentres, setCostCentres] = useState<FinancialsCostCentre[]>(mockCostCentres);
    const [loadingCostCentres, setLoadingCostCentres] = useState(false);
    const [nominalAccounts, setNominalAccounts] = useState<FinancialsNominalAccount[]>([]);
    const [loadingNominalAccounts, setLoadingNominalAccounts] = useState(false);
    const [languageSettings, setLanguageSettings] = useState<LanguageSetting[]>([]);
    const [savedLanguageSettings, setSavedLanguageSettings] = useState<LanguageSetting[]>([]);
    // Structured change tracking for better display
  interface FieldChange {
    field: string;
    oldValue: string | null;
    newValue: string;
  }

  interface SectionChanges {
    section: string;
    changes: FieldChange[];
  }

  interface TabChanges {
    tab: string;
    sections: SectionChanges[];
  }

  const [pendingChanges, setPendingChanges] = useState<TabChanges[]>([]);

  useEffect(() => {
    // Load saved options from localStorage
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      const loadedOptions = JSON.parse(savedOptionsStr);
      setOptions(loadedOptions);
      setSavedOptions(loadedOptions);
    } else {
      setSavedOptions(defaultOptions);
    }

    const savedIntegrations = localStorage.getItem('integrationConfig');
    if (savedIntegrations) {
      const config = JSON.parse(savedIntegrations);
      setIntegrationConfig(config);
      if (config.activeIntegration) {
        setSelectedIntegration(config.activeIntegration);
        setSavedIntegrationState({
          integration: config.activeIntegration,
          instance: config.integrations[config.activeIntegration]?.instanceId ?? ''
        });
        const integrationData = config.integrations[config.activeIntegration];
        if (integrationData?.instanceId) {
          setSelectedInstance(integrationData.instanceId);
        }
      }
    }
  }, []);

  useEffect(() => {
    // Warn user before leaving page with unsaved changes
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Define loadInstances before the useEffect that uses it
  const loadInstances = useCallback(async (integrationType: IntegrationType) => {
    setLoadingInstances(true);

    // Only fetch from API for access-financials integration
    if (integrationType === 'access-financials') {
      try {
        const response = await dataService.getFinancialsDatabaseInstances(integrationType);
        // API returns an array directly, not { instances: [...] }
        const instances = Array.isArray(response) ? response : (response?.instances ?? []);
        if (instances.length > 0) {
          setLoadedInstances(prev => ({
            ...prev,
            [integrationType]: instances.map((instance: any) => ({
              // API returns instanceId, not id
              id: instance.instanceId || instance.id,
              name: instance.name,
              environment: instance.environment as 'production' | 'staging' | 'test' | 'development' | undefined,
              connectionStatus: instance.connectionStatus ?? 'active',
              lastSync: instance.lastSync,
              description: instance.description || instance.organisationName
            }))
          }));
        }
      } catch (error) {
        console.error('Failed to load database instances:', error);
        // Fall back to mock data on error
        setLoadedInstances(prev => ({
          ...prev,
          [integrationType]: mockDatabaseInstances[integrationType] ?? []
        }));
      }
    } else {
      // Use mock data for other integrations
      setLoadedInstances(prev => ({
        ...prev,
        [integrationType]: mockDatabaseInstances[integrationType] ?? []
      }));
    }

    setLoadingInstances(false);
  }, []);

  // Load instances when selectedIntegration changes (handles localStorage loading case)
  useEffect(() => {
    if (selectedIntegration && !loadedInstances[selectedIntegration]?.length) {
      loadInstances(selectedIntegration);
    }
  }, [selectedIntegration, loadInstances, loadedInstances]);

  // Initialize from API settings when integrationSettings is available
  useEffect(() => {
    // Only run once on initial load when API settings are available
    if (hasInitializedFromApiRef.current) return;
    if (!integrationSettings || !integrationSettings.enabled) return;

    const integrationType = integrationSettings.integrationType as IntegrationType;
    if (!['access-dimensions', 'access-financials', 'xero', 'quickbooks'].includes(integrationType)) {
      return;
    }

    hasInitializedFromApiRef.current = true;

    // Set the integration type and instance from API settings
    setSelectedIntegration(integrationType);
    if (integrationSettings.instanceId) {
      setSelectedInstance(integrationSettings.instanceId);
    }
    setSavedIntegrationState({
      integration: integrationType,
      instance: integrationSettings.instanceId ?? ''
    });

      // Load instances for the dropdown
      loadInstances(integrationType);
    }, [integrationSettings, loadInstances]);

    // Load cost centres from Financials API
    useEffect(() => {
      const loadCostCentres = async () => {
        setLoadingCostCentres(true);
        try {
          const data = await financialsService.getCostCentres();
          if (data.length > 0) {
            setCostCentres(data);
          }
        } catch (error) {
          console.error('Failed to load cost centres:', error);
          // Keep using mock data on error
        } finally {
          setLoadingCostCentres(false);
        }
      };

      loadCostCentres();
    }, []);

    // Load nominal accounts from Financials API
    useEffect(() => {
      const loadNominalAccounts = async () => {
        setLoadingNominalAccounts(true);
        try {
          const data = await financialsService.getNominalAccounts();
          // Filter out "do not use" accounts for consistency with Asset Details
          const activeAccounts = data.filter(account => !account.doNotUse);
          if (activeAccounts.length > 0) {
            setNominalAccounts(activeAccounts);
          }
        } catch (error) {
          console.error('Failed to load nominal accounts:', error);
          // Keep using mock data on error
        } finally {
          setLoadingNominalAccounts(false);
        }
      };

      loadNominalAccounts();
    }, []);

    // Load language settings from API
    useEffect(() => {
      const loadLanguageSettings = async () => {
        try {
          const response = await dataService.getLanguageSettings();
          if (response.settings && response.settings.length > 0) {
            setLanguageSettings(response.settings);
            setSavedLanguageSettings(response.settings);
          }
        } catch (error) {
          console.error('Failed to load language settings:', error);
        }
      };

      loadLanguageSettings();
    }, []);

    const handleChange = (section: keyof AssetOptions, field: string, value: any) => {
    setOptions((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const handleIntegrationToggle = (integrationType: IntegrationType) => {
    const integration = integrations.find(i => i.id === integrationType);

    if (integration?.comingSoon) {
      return;
    }

    // If trying to select a different integration when one is already active
    if (selectedIntegration && selectedIntegration !== integrationType) {
      const confirmed = window.confirm(
        `Switching integrations will disconnect your current ${integrations.find(i => i.id === selectedIntegration)?.name} integration. Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    if (selectedIntegration === integrationType) {
      // Deselecting current integration
      setSelectedIntegration(null);
      setSelectedInstance('');
    } else {
      // Selecting new integration
      setSelectedIntegration(integrationType);
      setSelectedInstance('');
      // Load instances for this integration
      loadInstances(integrationType);
    }

    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const handleInstanceChange = (instanceId: string) => {
    setSelectedInstance(instanceId);
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Validate Details tab
    if (!options.defaultSettings.defaultMethod) {
      errors.push('Default Method is required');
    }
    if (options.defaultSettings.defaultPercentage === null || options.defaultSettings.defaultPercentage === undefined) {
      errors.push('Default Percentage is required');
    } else if (options.defaultSettings.defaultPercentage < 0 || options.defaultSettings.defaultPercentage > 100) {
      errors.push('Default Percentage must be between 0 and 100');
    }
    if (options.defaultSettings.defaultPeriodYears === null || options.defaultSettings.defaultPeriodYears === undefined) {
      errors.push('Default Period (Years) is required');
    } else if (options.defaultSettings.defaultPeriodYears < 0) {
      errors.push('Default Period (Years) must be 0 or greater');
    }
    if (!options.defaultSettings.defaultFrequency) {
      errors.push('Default Frequency is required');
    }

    // Validate Integrations tab
    if (selectedIntegration && !selectedInstance) {
      errors.push('Please select a database instance for the active integration');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const formatBoolean = (value: boolean) => value ? 'Enabled' : 'Disabled';

  const addChangeIfDifferent = <T,>(
    changes: FieldChange[],
    field: string,
    oldValue: T,
    newValue: T,
    formatter?: (value: T) => string
  ) => {
    if (oldValue !== newValue) {
      const format = formatter ?? ((v: T) => String(v));
      changes.push({
        field,
        oldValue: format(oldValue),
        newValue: format(newValue)
      });
    }
  };

  const getGeneralChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    addChangeIfDifferent(changes, 'Asset Auto Numbering', savedOptions.general.assetAutoNumbering, options.general.assetAutoNumbering, formatBoolean);
    addChangeIfDifferent(changes, 'Prevent Negative Values', savedOptions.general.doNotAllowNegativeBoughtValue, options.general.doNotAllowNegativeBoughtValue, formatBoolean);
    addChangeIfDifferent(changes, 'Default Base Value On', savedOptions.general.defaultBaseValueOn, options.general.defaultBaseValueOn, (v) => v === 'remaining' ? 'Remaining Postings' : 'Original Postings');
    addChangeIfDifferent(changes, 'Service Period Defaults', savedOptions.general.servicePeriodDefaults, options.general.servicePeriodDefaults, (v) => v.toString());
    return changes;
  };

  const getNominalChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    const promptKeys = ['depreciation', 'addition', 'revaluation', 'disposal', 'transfer'] as const;
    const promptLabels: Record<typeof promptKeys[number], string> = {
      depreciation: 'Prompt on Depreciation',
      addition: 'Prompt on Addition',
      revaluation: 'Prompt on Revaluation',
      disposal: 'Prompt on Disposal',
      transfer: 'Prompt on Transfer'
    };
    promptKeys.forEach(key => {
      addChangeIfDifferent(changes, promptLabels[key], savedOptions.promptNominalUpdate[key], options.promptNominalUpdate[key], formatBoolean);
    });
    addChangeIfDifferent(changes, 'Include Resource on Journals', savedOptions.updateNominal.includeResourceOnNominalJournals, options.updateNominal.includeResourceOnNominalJournals, formatBoolean);
    addChangeIfDifferent(changes, 'Summarise Journals by Default', savedOptions.updateNominal.summariseJournals, options.updateNominal.summariseJournals, formatBoolean);
    return changes;
  };

  const formatOptionalString = (value: string | undefined): string => {
    return value ?? 'Not set';
  };

  const getTransactionChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    addChangeIfDifferent(changes, 'Allow Zero Value Additions', savedOptions.transactions.allowZeroValueAdditions, options.transactions.allowZeroValueAdditions, formatBoolean);
    addChangeIfDifferent(changes, 'Force Depreciation Before Period End', savedOptions.transactions.forceAllDepreciationBeforePeriodEnd, options.transactions.forceAllDepreciationBeforePeriodEnd, formatBoolean);
    addChangeIfDifferent(changes, 'Preserve In-Period Depreciation', savedOptions.transactions.doNotReverseInPeriodDepreciation, options.transactions.doNotReverseInPeriodDepreciation, formatBoolean);
    return changes;
  };

  const getTransactionsTabNominalAccountChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    const nominalAccountLabels: Record<string, string> = {
      assetCost: 'Asset Cost',
      depreciationBS: 'Depreciation B/S',
      depreciationPL: 'Depreciation P&L',
      disposal: 'Disposal',
      revaluationBS: 'Revaluation B/S',
      revaluationPL: 'Revaluation P&L',
      disposalsBankAccount: 'Disposals Bank Account',
      additionsWIPBankAcc: 'Additions WIP / Bank Acc',
    };
    const nominalAccountKeys = Object.keys(nominalAccountLabels) as (keyof typeof savedOptions.transactions.nominalAccounts)[];
    nominalAccountKeys.forEach(key => {
      const oldValue = savedOptions.transactions.nominalAccounts[key];
      const newValue = options.transactions.nominalAccounts[key];
      if (oldValue !== newValue) {
        changes.push({
          field: nominalAccountLabels[key],
          oldValue: formatOptionalString(oldValue),
          newValue: formatOptionalString(newValue)
        });
      }
    });
    return changes;
  };

  const getTransactionsTabCostingChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    const costingLabels: Record<string, string> = {
      depreciation: 'Depreciation',
      addition: 'Addition',
      disposals: 'Disposals',
      revaluation: 'Revaluation',
    };
    const costingKeys = Object.keys(costingLabels) as (keyof typeof savedOptions.transactions.costing)[];
    costingKeys.forEach(key => {
      const oldValue = savedOptions.transactions.costing[key];
      const newValue = options.transactions.costing[key];
      if (oldValue !== newValue) {
        changes.push({
          field: costingLabels[key],
          oldValue: formatOptionalString(oldValue),
          newValue: formatOptionalString(newValue)
        });
      }
    });
    return changes;
  };

  const getTransactionsTabPostCostingChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    const postCostingLabels: Record<string, string> = {
      depreciation: 'Post Costing for Depreciation',
      additions: 'Post Costing for Additions',
      revaluations: 'Post Costing for Revaluations',
      disposals: 'Post Costing for Disposals',
    };
    const postCostingKeys = Object.keys(postCostingLabels) as (keyof typeof savedOptions.transactions.postCostingTransactions)[];
    postCostingKeys.forEach(key => {
      addChangeIfDifferent(changes, postCostingLabels[key], savedOptions.transactions.postCostingTransactions[key], options.transactions.postCostingTransactions[key], formatBoolean);
    });
    return changes;
  };

  const getTransactionsTabPostCreditChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    const postCreditLabels: Record<string, string> = {
      depreciation: 'Post CREDIT Depreciation to Costing',
      additions: 'Post CREDIT Additions to Costing',
      revaluations: 'Post CREDIT Revaluations to Costing',
      disposals: 'Post CREDIT Disposals to Costing',
    };
    const postCreditKeys = Object.keys(postCreditLabels) as (keyof typeof savedOptions.transactions.postCreditToCosting)[];
    postCreditKeys.forEach(key => {
      addChangeIfDifferent(changes, postCreditLabels[key], savedOptions.transactions.postCreditToCosting[key], options.transactions.postCreditToCosting[key], formatBoolean);
    });
    return changes;
  };

  const getDepreciationChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    addChangeIfDifferent(changes, 'Do Not Depreciate', savedOptions.defaultSettings.doNotDepreciate, options.defaultSettings.doNotDepreciate, formatBoolean);
    addChangeIfDifferent(changes, 'Default Method', savedOptions.defaultSettings.defaultMethod, options.defaultSettings.defaultMethod);
    addChangeIfDifferent(changes, 'Default Percentage', savedOptions.defaultSettings.defaultPercentage, options.defaultSettings.defaultPercentage, (v) => `${v}%`);
    addChangeIfDifferent(changes, 'Default Period (Years)', savedOptions.defaultSettings.defaultPeriodYears, options.defaultSettings.defaultPeriodYears, (v) => v.toString());
    addChangeIfDifferent(changes, 'Default Frequency', savedOptions.defaultSettings.defaultFrequency, options.defaultSettings.defaultFrequency);
    addChangeIfDifferent(changes, 'Force Transfers on Category Change', savedOptions.defaultSettings.forceTransfersOnChangingCategory, options.defaultSettings.forceTransfersOnChangingCategory, formatBoolean);
    return changes;
  };

  const getIntegrationChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    if (selectedIntegration !== savedIntegrationState.integration) {
      const oldIntegrationName = savedIntegrationState.integration
        ? integrations.find(i => i.id === savedIntegrationState.integration)?.name ?? null
        : null;
      const newIntegrationName = selectedIntegration
        ? integrations.find(i => i.id === selectedIntegration)?.name ?? 'None'
        : 'None';
      changes.push({
        field: 'Active Integration',
        oldValue: oldIntegrationName,
        newValue: newIntegrationName
      });
    }
    if (selectedIntegration && selectedInstance !== savedIntegrationState.instance) {
      const oldInstanceName = savedIntegrationState.instance && savedIntegrationState.integration
        ? loadedInstances[savedIntegrationState.integration]?.find(i => i.id === savedIntegrationState.instance)?.name ?? null
        : null;
      const newInstanceName = loadedInstances[selectedIntegration]?.find(i => i.id === selectedInstance)?.name;
      if (newInstanceName) {
        changes.push({
          field: 'Database Instance',
          oldValue: oldInstanceName,
          newValue: newInstanceName
        });
      }
    }
    return changes;
  };

  const getLanguageChanges = (): FieldChange[] => {
    const changes: FieldChange[] = [];
    
    // Compare current language settings with saved settings
    languageSettings.forEach((setting) => {
      const savedSetting = savedLanguageSettings.find(s => s.fieldKey === setting.fieldKey);
      if (savedSetting && setting.customLabel !== savedSetting.customLabel) {
        changes.push({
          field: setting.defaultLabel,
          oldValue: savedSetting.customLabel ?? setting.defaultLabel,
          newValue: setting.customLabel ?? setting.defaultLabel
        });
      }
    });
    
    return changes;
  };

  const generateChangeSummary = (): TabChanges[] => {
    const tabChanges: TabChanges[] = [];
    const detailsSections: SectionChanges[] = [];

    const generalChanges = getGeneralChanges();
    if (generalChanges.length > 0) {
      detailsSections.push({ section: 'General Settings', changes: generalChanges });
    }

    const nominalChanges = getNominalChanges();
    if (nominalChanges.length > 0) {
      detailsSections.push({ section: 'Nominal Update Settings', changes: nominalChanges });
    }

    const transactionChanges = getTransactionChanges();
    if (transactionChanges.length > 0) {
      detailsSections.push({ section: 'Transaction Settings', changes: transactionChanges });
    }

    const depreciationChanges = getDepreciationChanges();
    if (depreciationChanges.length > 0) {
      detailsSections.push({ section: 'Default Depreciation Settings', changes: depreciationChanges });
    }

    if (detailsSections.length > 0) {
      tabChanges.push({ tab: 'Details', sections: detailsSections });
    }

    // Transactions tab changes
    const transactionsTabSections: SectionChanges[] = [];

    const nominalAccountChanges = getTransactionsTabNominalAccountChanges();
    if (nominalAccountChanges.length > 0) {
      transactionsTabSections.push({ section: 'Nominal Accounts', changes: nominalAccountChanges });
    }

    const costingChanges = getTransactionsTabCostingChanges();
    if (costingChanges.length > 0) {
      transactionsTabSections.push({ section: 'Costing', changes: costingChanges });
    }

    const postCostingChanges = getTransactionsTabPostCostingChanges();
    if (postCostingChanges.length > 0) {
      transactionsTabSections.push({ section: 'Post Costing Transactions', changes: postCostingChanges });
    }

    const postCreditChanges = getTransactionsTabPostCreditChanges();
    if (postCreditChanges.length > 0) {
      transactionsTabSections.push({ section: 'Post CREDIT to Costing', changes: postCreditChanges });
    }

    if (transactionsTabSections.length > 0) {
      tabChanges.push({ tab: 'Transactions', sections: transactionsTabSections });
    }

    const integrationChanges = getIntegrationChanges();
    if (integrationChanges.length > 0) {
      tabChanges.push({ tab: 'Integrations', sections: [{ section: 'Financial System Integration', changes: integrationChanges }] });
    }

    const languageChanges = getLanguageChanges();
    if (languageChanges.length > 0) {
      tabChanges.push({ tab: 'Language', sections: [{ section: 'Language Label Changes', changes: languageChanges }] });
    }

    return tabChanges;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Generate change summary and show confirmation modal
    const changes = generateChangeSummary();
    setPendingChanges(changes);
    setShowConfirmModal(true);
  };

    const handleConfirmSave = async () => {
      setIsSaving(true);

      try {
        // Save options to localStorage
        localStorage.setItem('assetOptions', JSON.stringify(options));
        setSavedOptions(options);

        // Also save to backend API (for database persistence)
        try {
          await dataService.updateSystemOptions({
            configType: 'STANDARD',
            isMultiEntity: false,
            autoNumbering: {
              enabled: options.general.assetAutoNumbering,
              numberOfChars: 10,
              currentNumber: 1,
            },
            validation: {
              noNegativeBoughtValue: options.general.doNotAllowNegativeBoughtValue,
            },
            defaults: {
              baseValueOn: options.general.defaultBaseValueOn,
              servicePeriod: options.general.servicePeriodDefaults,
              forceTransfersOnChangingCategory: options.defaultSettings.forceTransfersOnChangingCategory,
            },
            transactions: {
              allowZeroValueAdditions: options.transactions.allowZeroValueAdditions,
              forceAllDepreciationBeforePeriodEnd: options.transactions.forceAllDepreciationBeforePeriodEnd,
              doNotReverseInPeriodDepreciation: options.transactions.doNotReverseInPeriodDepreciation,
              nominalAccounts: {
                assetCost: options.transactions.nominalAccounts.assetCost ?? null,
                depreciationBS: options.transactions.nominalAccounts.depreciationBS ?? null,
                depreciationPL: options.transactions.nominalAccounts.depreciationPL ?? null,
                disposal: options.transactions.nominalAccounts.disposal ?? null,
                revaluationBS: options.transactions.nominalAccounts.revaluationBS ?? null,
                revaluationPL: options.transactions.nominalAccounts.revaluationPL ?? null,
                disposalsBankAccount: options.transactions.nominalAccounts.disposalsBankAccount ?? null,
                additionsWIPBankAcc: options.transactions.nominalAccounts.additionsWIPBankAcc ?? null,
              },
              costing: {
                depreciation: options.transactions.costing.depreciation ?? null,
                addition: options.transactions.costing.addition ?? null,
                disposals: options.transactions.costing.disposals ?? null,
                revaluation: options.transactions.costing.revaluation ?? null,
              },
              postCostingTransactions: {
                depreciation: options.transactions.postCostingTransactions.depreciation,
                additions: options.transactions.postCostingTransactions.additions,
                revaluations: options.transactions.postCostingTransactions.revaluations,
                disposals: options.transactions.postCostingTransactions.disposals,
              },
              postCreditToCosting: {
                depreciation: options.transactions.postCreditToCosting.depreciation,
                additions: options.transactions.postCreditToCosting.additions,
                revaluations: options.transactions.postCreditToCosting.revaluations,
                disposals: options.transactions.postCreditToCosting.disposals,
              },
            },
          });
        } catch (apiError) {
          // Log the error but don't fail the save - localStorage is the primary storage for now
          console.warn('Failed to save to backend API (localStorage save succeeded):', apiError);
        }

        // Save language settings to backend API
        if (languageSettings.length > 0) {
          try {
            // Use case-insensitive comparison for field keys
            const settingsToUpdate = languageSettings
              .filter(s => s.customLabel !== savedLanguageSettings.find(saved => 
                saved.fieldKey.toLowerCase() === s.fieldKey.toLowerCase())?.customLabel)
              .map(s => ({
                fieldKey: s.fieldKey,
                customLabel: s.customLabel,
              }));
            
            if (settingsToUpdate.length > 0) {
              const response = await dataService.updateLanguageSettings(settingsToUpdate);
              if (response.settings) {
                setLanguageSettings(response.settings);
                setSavedLanguageSettings(response.settings);
              }
            }
          } catch (langError) {
            console.warn('Failed to save language settings to backend API:', langError);
          }
        }

        // Save integration config
        const newIntegrationConfig: IntegrationConfig = {
          activeIntegration: selectedIntegration ?? undefined,
          integrations: {}
        };

        if (selectedIntegration && selectedInstance) {
          // Get the instance name from loaded instances
          const instanceName = loadedInstances[selectedIntegration]?.find(
            i => i.id === selectedInstance
          )?.name || selectedInstance;

          newIntegrationConfig.integrations[selectedIntegration] = {
            enabled: true,
            instanceId: selectedInstance,
            lastSync: new Date().toISOString()
          };

          // Save to backend API and update context
          await setIntegrationContextInstance(
            selectedIntegration,
            selectedInstance,
            instanceName,
            true
          );
        } else if (savedIntegrationState.integration) {
          // Integration was previously enabled but is now being disabled
          // Clear the integration on the backend
          await clearIntegrationContextInstance(savedIntegrationState.integration);
        }

        localStorage.setItem('integrationConfig', JSON.stringify(newIntegrationConfig));
        setIntegrationConfig(newIntegrationConfig);
        setSavedIntegrationState({
          integration: selectedIntegration,
          instance: selectedInstance
        });

        setHasUnsavedChanges(false);
        setShowConfirmModal(false);

        // Show success toast
        toast.success('Configuration updated successfully');
      } catch (error) {
        console.error('Failed to save integration settings:', error);
        toast.error('Failed to save integration settings. Please try again.');
      } finally {
        setIsSaving(false);
      }
    };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirmModal(true);
      return;
    }
  };

  const handleConfirmCancel = () => {
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      const loadedOptions = JSON.parse(savedOptionsStr);
      setOptions(loadedOptions);
      setSavedOptions(loadedOptions);
    } else {
      setOptions(defaultOptions);
      setSavedOptions(defaultOptions);
    }

    const savedIntegrations = localStorage.getItem('integrationConfig');
    if (savedIntegrations) {
      const config = JSON.parse(savedIntegrations);
      setIntegrationConfig(config);
      if (config.activeIntegration) {
        setSelectedIntegration(config.activeIntegration);
        const integrationData = config.integrations[config.activeIntegration];
        if (integrationData?.instanceId) {
          setSelectedInstance(integrationData.instanceId);
        }
        setSavedIntegrationState({
          integration: config.activeIntegration,
          instance: integrationData?.instanceId ?? ''
        });
      } else {
        setSelectedIntegration(null);
        setSelectedInstance('');
        setSavedIntegrationState({ integration: null, instance: '' });
      }
    } else {
      setIntegrationConfig(defaultIntegrationConfig);
      setSelectedIntegration(null);
      setSelectedInstance('');
      setSavedIntegrationState({ integration: null, instance: '' });
    }

    // Reset language settings to saved state
    setLanguageSettings(savedLanguageSettings);

    setHasUnsavedChanges(false);
    setValidationErrors([]);
    setShowCancelConfirmModal(false);

    toast.info('Changes discarded');
  };

  const handleResetToDefaults = () => {
    setOptions(defaultOptions);
    setHasUnsavedChanges(true);
    setShowSuccessAlert(false);
  };

  const getEnvironmentBadgeVariant = (env?: string) => {
    switch (env) {
      case 'production':
        return 'default';
      case 'staging':
        return 'secondary';
      case 'test':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Settings2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Asset Options</h2>
              <p className="text-sm text-muted-foreground">
                Configure asset settings and financial system integrations
              </p>
            </div>
          </div>
          <EvoFeatureFeedbackCollector feature="Asset Options" />
        </div>
      </div>

            {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Vertical Tabs Sidebar */}
        <div className="w-64 border-r bg-card" style={{ borderColor: 'oklch(0.928 0.006 264.531)' }}>
          <div className="p-4 space-y-1">
            <button
              onClick={() => setActiveTab('details')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'details'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Settings2 className="h-4 w-4" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'transactions'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <FileEdit className="h-4 w-4" />
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('periods')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'periods'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Database className="h-4 w-4" />
              Periods
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'integrations'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Plug2 className="h-4 w-4" />
              Integrations
            </button>
            <button
              onClick={() => setActiveTab('language')}
              className={cn(
                "flex w-full items-center justify-start gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors",
                activeTab === 'language'
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Languages className="h-4 w-4" />
              Language
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-auto p-6 pb-32">
          <div className="container mx-auto max-w-7xl">
            {showSuccessAlert && (
              <Alert className="mb-6 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  {successMessage ?? 'Settings saved successfully!'}
                </AlertDescription>
              </Alert>
            )}

            {validationErrors.length > 0 && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-inside list-disc space-y-1">
                    {validationErrors.map((error) => (
                      <li key={`error-${error}`}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              <DetailsTab
                options={options}
                onOptionsChange={handleChange}
                setActiveTab={setActiveTab}
              />
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="space-y-6">
              <TransactionsTab
                options={options}
                onOptionsChange={setOptions}
                setHasUnsavedChanges={setHasUnsavedChanges}
                nominalAccounts={nominalAccounts}
                costCentres={costCentres}
                loadingCostCentres={loadingCostCentres}
                loadingNominalAccounts={loadingNominalAccounts}
              />
            </TabsContent>

            {/* Periods Tab */}
            <TabsContent value="periods" className="space-y-6">
              <PeriodsTab
                options={options}
                onOptionsChange={setOptions}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </TabsContent>

            {/* Integrations Tab */}
            <TabsContent value="integrations" className="space-y-6">
              <IntegrationsTab
                integrations={integrations}
                selectedIntegration={selectedIntegration}
                selectedInstance={selectedInstance}
                loadingInstances={loadingInstances}
                loadedInstances={loadedInstances}
                onIntegrationToggle={handleIntegrationToggle}
                onInstanceChange={handleInstanceChange}
                getEnvironmentBadgeVariant={getEnvironmentBadgeVariant}
              />
            </TabsContent>

            {/* Language Tab */}
            <TabsContent value="language" className="space-y-6">
              <LanguageTab
                languageSettings={languageSettings}
                onLanguageSettingsChange={setLanguageSettings}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </div>

      {/* Fixed Footer - Pinned to bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-background border-t shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!hasUnsavedChanges}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5 text-primary" />
              Confirm Configuration Changes
            </DialogTitle>
            <DialogDescription>
              Review the changes you're about to save to your asset configuration.
            </DialogDescription>
          </DialogHeader>

          {pendingChanges.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-6">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No changes detected
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 pb-3 flex items-center justify-between flex-shrink-0 border-b">
                <p className="text-sm font-medium">
                  {pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0)} field{pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0) === 1 ? '' : 's'} modified across {pendingChanges.length} tab{pendingChanges.length === 1 ? '' : 's'}
                </p>
                <Badge variant="secondary">
                  {pendingChanges.reduce((total, tab) => total + tab.sections.reduce((sum, section) => sum + section.changes.length, 0), 0)}
                </Badge>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
                <div className="space-y-4">
                  {pendingChanges.map((tabChange, tabIndex) => (
                    <div key={tabIndex} className="space-y-3">
                      {/* Tab Header */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <Settings2 className="h-4 w-4 text-primary" />
                        <h3 className="font-semibold text-sm">{tabChange.tab}</h3>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {tabChange.sections.reduce((sum, section) => sum + section.changes.length, 0)} change{tabChange.sections.reduce((sum, section) => sum + section.changes.length, 0) === 1 ? '' : 's'}
                        </Badge>
                      </div>

                      {/* Sections */}
                      <div className="space-y-3 pl-2">
                        {tabChange.sections.map((sectionChange, sectionIndex) => (
                          <div key={sectionIndex} className="space-y-2">
                            {/* Section Header */}
                            <div className="flex items-center gap-2">
                              <div className="h-px w-3 bg-border" />
                              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {sectionChange.section}
                              </h4>
                            </div>

                            {/* Field Changes */}
                            <div className="space-y-2 pl-5">
                              {sectionChange.changes.map((fieldChange, fieldIndex) => (
                                <div
                                  key={fieldIndex}
                                  className="rounded-md border bg-card p-3 space-y-1.5"
                                >
                                  <div className="text-xs font-medium text-foreground">
                                    {fieldChange.field}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                    <div className="flex-1 flex items-center gap-1.5">
                                      <span className="text-muted-foreground">From:</span>
                                      <code className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                                        {fieldChange.oldValue ?? '�'}
                                      </code>
                                    </div>
                                    <ArrowRight className="h-3 w-3 text-primary flex-shrink-0" />
                                    <div className="flex-1 flex items-center gap-1.5">
                                      <span className="text-muted-foreground">To:</span>
                                      <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono font-medium">
                                        {fieldChange.newValue}
                                      </code>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter className="px-6 py-4 gap-2 sm:gap-0 flex-shrink-0 border-t">
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={pendingChanges.length === 0}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Confirm & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <AlertDialog open={showCancelConfirmModal} onOpenChange={setShowCancelConfirmModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Discard Unsaved Changes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your asset configuration. If you cancel now, all modifications will be lost and the UI will be reset to the last saved state.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
